import React, { useState, useEffect, useRef } from 'react';

const LOADING_MESSAGES = [
    'Unfolding the map...',
    'Plotting your drops on the grid...',
    'Doing the maths (it\'s a lot)...',
    'Finding the scenic route... just kidding, the fast one...',
    'Pinging postcodes into orbit...',
    'The AI is having a think...',
    'Convincing the truck to go left first...',
    'Crunching distances at the speed of light...',
    'Avoiding all known roadworks...',
    'Nearly there - tidying up the route...',
];

const MIN_LOADING_MS = 10000;

const RouteOptimiseModal = ({ theme, isOpen, onClose, onAccept, optimised, driverName }) => {
    const [showMap, setShowMap] = useState(false);
    const [msgIndex, setMsgIndex] = useState(0);
    const [barWidth, setBarWidth] = useState(8);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const msgTimer = useRef(null);
    const barTimer = useRef(null);

    const shuffledRef = useRef([]);

    const isLoading = isOpen && !optimised;

    useEffect(() => {
        if (isLoading) {
            // Shuffle the full message list once so every message appears exactly once
            const indices = LOADING_MESSAGES.map((_, i) => i);
            for (let i = indices.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [indices[i], indices[j]] = [indices[j], indices[i]];
            }
            shuffledRef.current = indices;
            setMsgIndex(indices[0]);
            setBarWidth(8);
            let pos = 0;
            msgTimer.current = setInterval(() => {
                pos = (pos + 1) % shuffledRef.current.length;
                setMsgIndex(shuffledRef.current[pos]);
            }, 3000);
            barTimer.current = setInterval(() => {
                setBarWidth(w => Math.min(w + Math.random() * 9 + 3, 88));
            }, 900);
        }
        return () => {
            clearInterval(msgTimer.current);
            clearInterval(barTimer.current);
        };
    }, [isLoading]);

    useEffect(() => {
        if (!showMap || !optimised || optimised.length === 0) return;
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const initMap = () => {
            if (!mapRef.current) return;
            const bounds = new window.google.maps.LatLngBounds();
            const mapInstance = new window.google.maps.Map(mapRef.current, { zoom: 10, mapTypeId: 'roadmap' });
            mapInstanceRef.current = mapInstance;
            const path = [];
            optimised.forEach((stop, i) => {
                const pos = { lat: stop.lat, lng: stop.lng };
                path.push(pos);
                bounds.extend(pos);
                new window.google.maps.Marker({
                    position: pos,
                    map: mapInstance,
                    label: { text: String(i + 1), color: '#fff', fontWeight: 'bold', fontSize: '12px' },
                    title: (i + 1) + '. ' + stop.job + ' (' + stop.postcode + ')',
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 16,
                        fillColor: i === 0 ? '#22c55e' : i === optimised.length - 1 ? '#ef4444' : '#3b82f6',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 2,
                    },
                });
            });
            new window.google.maps.Polyline({ path, geodesic: true, strokeColor: '#6366f1', strokeOpacity: 0.7, strokeWeight: 2, map: mapInstance });
            mapInstance.fitBounds(bounds);
        };
        if (window.google) { initMap(); } else {
            const script = document.createElement('script');
            script.src = 'https://maps.googleapis.com/maps/api/js?key=' + apiKey;
            script.async = true;
            script.defer = true;
            script.onload = initMap;
            document.head.appendChild(script);
        }
    }, [showMap, optimised]);

    if (!isOpen) return null;

    const isDark = theme === 'dark';
    const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const container = {
        background: isDark ? '#1e1e2e' : '#fff',
        color: isDark ? '#e0e0e0' : '#222',
        borderRadius: 12,
        padding: 28,
        width: '90vw',
        maxWidth: 640,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
        border: isDark ? '1px solid rgba(99,102,241,0.45)' : '1px solid rgba(99,102,241,0.3)',
    };

    if (isLoading) {
        return (
            <div style={overlay}>
                <div style={{ ...container, maxWidth: 440, textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 8px' }}>Optimising Route</h3>
                    <p style={{ margin: '0 0 24px', opacity: 0.55, fontSize: 13 }}>{driverName}</p>
                    <div style={{ background: isDark ? '#2a2a3e' : '#e9e9f5', borderRadius: 8, height: 10, marginBottom: 20, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: barWidth + '%', borderRadius: 8, background: 'linear-gradient(90deg, #6366f1, #818cf8)', transition: 'width 0.8s ease' }} />
                    </div>
                    <p style={{ fontSize: 14, minHeight: 22, margin: 0, opacity: 0.85 }}>{LOADING_MESSAGES[msgIndex]}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={container} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 6px' }}>Optimised Route &mdash; {driverName}</h3>
                <p style={{ margin: '0 0 16px', opacity: 0.6, fontSize: 13 }}>AI has calculated the most efficient drop order. Accept to apply, or Decline to keep the current order.</p>
                <ol style={{ paddingLeft: 22, margin: '0 0 16px' }}>
                    {optimised.map((stop, i) => (
                        <li key={stop.route_uuid} style={{ marginBottom: 8 }}>
                            <strong>{stop.job}</strong>
                            <span style={{ opacity: 0.6, marginLeft: 8, fontSize: 13 }}>{stop.postcode}</span>
                            {i === 0 && <span style={{ marginLeft: 8, fontSize: 11, color: '#22c55e', fontWeight: 700 }}>FIRST</span>}
                            {i === optimised.length - 1 && <span style={{ marginLeft: 8, fontSize: 11, color: '#ef4444', fontWeight: 700 }}>LAST</span>}
                        </li>
                    ))}
                </ol>
                <button onClick={() => setShowMap(v => !v)} style={{ marginBottom: 12, padding: '6px 16px', borderRadius: 6, border: '1px solid #6366f1', background: 'transparent', color: '#6366f1', cursor: 'pointer', fontSize: 13 }}>
                    {showMap ? 'Hide Map' : 'View on Map'}
                </button>
                {showMap && <div ref={mapRef} style={{ width: '100%', height: 340, borderRadius: 8, marginBottom: 16, background: '#ccc' }} />}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '8px 22px', borderRadius: 6, border: '1px solid #aaa', background: 'transparent', cursor: 'pointer', color: isDark ? '#ccc' : '#555' }}>Decline</button>
                    <button onClick={() => { onAccept(optimised); onClose(); }} style={{ padding: '8px 22px', borderRadius: 6, border: 'none', background: '#22c55e', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Accept</button>
                </div>
            </div>
        </div>
    );
};

export default RouteOptimiseModal;
